import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/str-stores/my-account/schedule/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyAccountScheduleInput = paths[url]['get']['parameters']['path'];
export interface GetMyAccountScheduleSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get store by uuid.
 */
export const useGetMyAccountSchedule = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setMyAccountStoreSchedule, setIsLoadingMyAccountStoreSchedule } = useData();

    const request = async (props: GetMyAccountScheduleInput) => {
        setIsLoadingMyAccountStoreSchedule(true);

        const sessionId = getSessionId();

        try {
            const commonQueryString = new URLSearchParams(getCommonParameters()).toString();
            const sessionQueryString = `session_id=${encodeURIComponent(sessionId)}`;
            const fullQueryString = `${commonQueryString}&${sessionQueryString}`;

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid)}?${fullQueryString}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...props }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetMyAccountScheduleSuccessResponse = await response.json();

            setMyAccountStoreSchedule(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingMyAccountStoreSchedule(false);
        }
    };

    return { request };
};
